import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Save, Eye, EyeOff, Plus, Lock, Unlock, Maximize2, Minimize2, Palette, Ruler, RefreshCw, Undo2, Redo2, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ChapterCard } from "@/components/manual/ChapterCard";
import { RichBodyEditor } from "@/components/manual/RichBodyEditor";
import { BookPreviewPane } from "@/components/manual/BookPreviewPane";
import { countWords, readTime } from "@/components/manual/helpers";
import { Section, SectionCard } from "@/components/manual/SectionCard";
import { BookInfoPresetControls, PublishersKitBar, BOOK_INFO_FIELDS } from "@/components/manual/PublishersKit";
import { computeChapterLabels, inferKind } from "@/components/manual/tocNumbering";
import { useEditHistory } from "@/components/manual/useEditHistory";
import { useStructureHistory } from "@/components/manual/useStructureHistory";
import { ReadingRuler } from "@/components/manual/ReadingRuler";
import DesignControlsPanel from "@/components/DesignControlsPanel";
import { useDesignFontWarmup } from "@/hooks/useDesignFontWarmup";

// -----------------------------------------------------------------------------
export default function ManualCompose({ book, patch, onClose, reloadBook, onReclassifyChapter, activeIdx = 0 }) {
  useDesignFontWarmup();
  const bookId = book?.id;
  // iter-83 — hide/show the Design Controls right column. Author can
  // collapse it when they want a wider preview surface.
  // iter-90 — persist per browser so the pane state survives reloads
  // and the Design panel isn't accidentally "trapped" hidden when the
  // author also hides the Preview column.
  const [showDesign, setShowDesign] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("mc-show-design");
    return v === null ? true : v === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mc-show-design", showDesign ? "1" : "0");
    }
  }, [showDesign]);
  // iter-90 — full-width Manual Entry toggle. Removes the max-w-3xl
  // cap when preview is off so authors can spread the editor across
  // the whole modal on wide screens.
  const [fullWidth, setFullWidth] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("mc-full-width") === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mc-full-width", fullWidth ? "1" : "0");
    }
  }, [fullWidth]);
  // iter-92 — Reading Ruler toggle. Movable horizontal guide over the
  // editor canvas that shows an approximate page number so authors
  // can eyeball chapter length before exporting.
  const [showRuler, setShowRuler] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("mc-show-ruler") === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mc-show-ruler", showRuler ? "1" : "0");
    }
  }, [showRuler]);
  const editorScrollRef = useRef(null);
  // iter-81 — Design Controls snapshot. Local mirror of the server's
  // resolved snapshot; DesignControlsPanel debounce-saves to the API
  // and notifies us here so the preview updates instantly.
  const [designControls, setDesignControls] = useState(
    () => book?.design_controls || {}
  );
  useEffect(() => {
    if (book?.design_controls) setDesignControls(book.design_controls);
  }, [book?.design_controls]);

  // Inline style to mirror the resolved snapshot inside the preview
  // pane. Uses the pt sizes + mm spacings from ``_computed`` so the
  // WYSIWYG matches the exported PDF as closely as CSS-in-JS allows.
  const designPreviewStyle = useMemo(() => {
    const dc = designControls || {};
    const c = dc._computed || {};
    return {
      fontFamily: c.body_font_stack || undefined,
      fontSize: c.body_size_pt ? `${c.body_size_pt}pt` : undefined,
      lineHeight: dc.line_height || undefined,
      textAlign: dc.body_align || undefined,
    };
  }, [designControls]);

  // iter-39: history hook — records a snapshot before each Save so a
  // batch of manual edits can be reverted from the BookEditor sidebar.
  // The history panel lives on the main editor, not this modal.
  const history = useEditHistory(book.id, null);
  // Book info state
  const [info, setInfo] = useState({
    title: book.title || "",
    subtitle: book.subtitle || "",
    author: book.author || "",
    publisher: book.publisher || "",
    imprint: book.imprint || "",
    copyright_year: book.copyright_year || "",
    email: book.email || "",
    isbn: book.isbn || "",
  });
  const [intro, setIntro] = useState(book.introduction || "");
  const [tocNote, setTocNote] = useState(book.toc_note || "");
  // iter-107 — author-editable TOC page heading.
  const [tocHeading, setTocHeading] = useState(book.toc_heading || "Table of Contents");
  const [front, setFront] = useState(() => (book.front_matter_sections || []).map((s) => ({ ...s })));
  const [back, setBack] = useState(() => (book.back_matter_sections || []).map((s) => ({ ...s })));
  const [chapters, setChapters] = useState(() =>
    (book.chapters || []).map((c) => ({
      _rid: Math.random().toString(36).slice(2, 10),
      title: c.title || "", subtitle: c.subtitle || "",
      description: c.description || "", content: c.content || "",
      // iter-36: per-row kind ("chapter" | "part" | "section") + optional
      // label override. Legacy rows have neither; on load we run inferKind()
      // once so the kind is frozen from the initial title regex — otherwise
      // editing the title (e.g. removing the word "Part") would flip the
      // row's badge from Part → Chapter mid-typing.  (iter-89 fix)
      kind: c.kind || inferKind(c), label: c.label || "",
      // iter-71/80: preserve the structural fields from the source book so
      // Manual Entry round-trips ``_kind`` (used by the sidebar) and
      // ``subtopics`` (bulleted lead-in list) without wiping them on save.
      _kind: c._kind || null,
      subtopics: Array.isArray(c.subtopics) ? [...c.subtopics] : (c.subtopics || null),
      // iter-71: pass through the imported chapter's source_index so the
      // lock toggle (per row + bulk) can key its API calls on the same
      // block the left Contents sidebar highlights.
      chapter_source_index: c.chapter_source_index ?? null,
    }))
  );
  // iter-98 — Undo/redo for STRUCTURAL edits (add / remove / reorder /
  // kind change). Content typing bypasses the history stack so
  // Ctrl+Z inside a text field still runs native input undo.
  const structHistory = useStructureHistory(chapters);
  // Keep the caller-owned `chapters` state in lockstep with the history
  // cursor. `structHistory.chapters` is always the source of truth.
  useEffect(() => { setChapters(structHistory.chapters); }, [structHistory.chapters]);
  // Bind global Cmd/Ctrl+Z / Cmd+Shift+Z / Ctrl+Y for structural undo/redo.
  useEffect(() => structHistory.bindShortcuts(), [structHistory]);
  const [busy, setBusy] = useState(false);
  const [chapterNumberingStyle, setChapterNumberingStyle] = useState(
    book.chapter_numbering_style || "arabic");
  // iter-38: Preview pane is now pinned open by default so authors watch
  // the WYSIWYG update as they type. Persisted per browser via
  // localStorage so a user who turns it off doesn't get re-opened on
  // every book open.
  const [showPreview, setShowPreview] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("mc-show-preview");
    return v === null ? true : v === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mc-show-preview", showPreview ? "1" : "0");
    }
  }, [showPreview]);
  const [dragIdx, setDragIdx] = useState(null);       // index of the chapter currently being dragged
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Chapter helpers — iter-98 wires STRUCTURAL mutations through
  // `structHistory.commit` so Undo/Redo captures them. Content-only
  // edits (title / subtitle / body typing) route through
  // `structHistory.replace` to avoid flooding the stack and to keep
  // the native input caret undo intact.
  const addChapter = () => structHistory.commit((prev) => [...prev, {
    _rid: Math.random().toString(36).slice(2, 10),
    title: `Chapter ${prev.filter((c) => (c.kind || "chapter") === "chapter").length + 1}`,
    subtitle: "", description: "", content: "", kind: "chapter", label: "",
    _autoOpen: true,
  }]);
  const addPart = () => structHistory.commit((prev) => [...prev, {
    _rid: Math.random().toString(36).slice(2, 10),
    title: "Part —", subtitle: "", description: "", content: "",
    kind: "part", label: "", _autoOpen: true,
  }]);
  const addSection = () => structHistory.commit((prev) => [...prev, {
    _rid: Math.random().toString(36).slice(2, 10),
    title: "Resources", subtitle: "", description: "", content: "",
    kind: "section", label: "", _autoOpen: true,
  }]);
  // iter-97 — Appendix: same shape as a section BUT with a blank
  // badge and no auto label prefix. Great for "Appendix A", "Notes",
  // "Acknowledgments" — anywhere authors want a labeless entry in the
  // flow AFTER the final chapter.
  const addAppendix = () => structHistory.commit((prev) => [...prev, {
    _rid: Math.random().toString(36).slice(2, 10),
    title: "Appendix", subtitle: "", description: "", content: "",
    kind: "appendix", label: "", _autoOpen: true,
  }]);
  // iter-107 — Preface: mirror of Appendix but positioned at the TOP
  // of the chapter list so it renders BEFORE Chapter 1. Lets authors
  // add multiple front-matter narratives (custom preface, "How to
  // use this book", translator's note, etc.) beyond the single
  // preset preface in the FRONT MATTER section list.
  const addPreface = () => structHistory.commit((prev) => {
    const row = {
      _rid: Math.random().toString(36).slice(2, 10),
      title: "Preface", subtitle: "", description: "", content: "",
      kind: "preface", label: "", _autoOpen: true,
    };
    // Insert after any existing preface rows so new prefaces stack
    // in insert order at the very top of the list.
    let insertAt = 0;
    while (insertAt < prev.length
        && (prev[insertAt]?.kind || "").toLowerCase() === "preface") {
      insertAt += 1;
    }
    const next = [...prev];
    next.splice(insertAt, 0, row);
    return next;
  });
  const removeChapter = (idx) =>
    structHistory.commit((prev) => prev.filter((_, i) => i !== idx));
  const moveChapter = (idx, dir) => structHistory.commit((prev) => {
    const to = idx + dir;
    if (to < 0 || to >= prev.length) return prev;
    const out = [...prev];
    [out[idx], out[to]] = [out[to], out[idx]];
    return out;
  });
  const reorderChapter = (fromIdx, toIdx) => structHistory.commit((prev) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0
        || fromIdx >= prev.length || toIdx >= prev.length) return prev;
    const out = [...prev];
    const [moved] = out.splice(fromIdx, 1);
    out.splice(toIdx, 0, moved);
    return out;
  });
  // iter-98 — split content vs. structural patches. A `kind` change
  // flips the badge/numbering (structural). Everything else (title /
  // subtitle / body / description / label / subtopics / etc.) is
  // content — no history snapshot.
  const setChapterAt = (idx, patchIt) => {
    const isStructural = patchIt && Object.prototype.hasOwnProperty.call(patchIt, "kind");
    const applier = (prev) => prev.map((c, i) => i === idx ? { ...c, ...patchIt } : c);
    if (isStructural) structHistory.commit(applier);
    else structHistory.replace(applier);
  };

  // iter-90 — one-shot heal for legacy books whose Parts / Sections
  // live in the DB with kind:"". At load-time `inferKind()` fills
  // those in locally; here we silently persist the inferred kinds
  // back so the badge is stable even after a full-page reload from
  // a fresh device. Runs once per book open, only when at least one
  // row was actually healed. No toast — this is auto-repair.
  const healedRef = React.useRef(false);
  useEffect(() => {
    if (healedRef.current) return;
    const original = book.chapters || [];
    if (!original.length) return;
    const needsHeal = original.some((c) => !(c.kind || "").trim() && inferKind(c) !== "chapter");
    if (!needsHeal) { healedRef.current = true; return; }
    healedRef.current = true;
    // Fire-and-forget PATCH. Bypasses the parent `patch` helper so a
    // network hiccup during healing doesn't toast an error at the user.
    // Uses the freshly-materialised state; healing retries on next open
    // if it fails. The book state stays in-memory-authoritative until
    // the user actually saves.
    (async () => {
      try {
        await api.patch(`/books/${book.id}`, {
          chapters: chapters.map(({ _rid, _autoOpen, ...c }) => c),
        });
      } catch (err) {
        console.warn("kind auto-heal failed:", err?.message || err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book.id]);


  // ---------- Reusable presets ----------
  const [presets, setPresets] = useState([]);   // [{id, kind, key, title, content, name, fields}]
  const [kits, setKits] = useState([]);         // [{id, name, ..., is_shared, is_mine, team_id}]
  const [team, setTeam] = useState(null);       // {id, name} — null if user is not on a team
  // iter-99 — Workspaces the caller can share kits into.
  const [workspaces, setWorkspaces] = useState([]);

  const loadPresets = useCallback(async () => {
    try {
      const { data } = await api.get("/manual-presets");
      setPresets(data || []);
    } catch (err) {
      console.warn("loadPresets failed:", err?.message || err);
    }
  }, []);
  const loadKits = useCallback(async () => {
    try {
      const { data } = await api.get("/preset-kits");
      setKits(data || []);
    } catch (err) {
      console.warn("loadKits failed:", err?.message || err);
    }
  }, []);
  const loadTeam = useCallback(async () => {
    try {
      const { data } = await api.get("/ph360/team");
      setTeam(data?.team || null);
    } catch (err) {
      // 401 for non-members is normal
      setTeam(null);
    }
  }, []);
  // iter-99 — load workspaces so the "Share with…" dropdown in the
  // Kit modal can offer every workspace the caller belongs to.
  const loadWorkspaces = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces/mine");
      const list = Array.isArray(data) ? data : (data?.workspaces || []);
      setWorkspaces(list);
    } catch (err) {
      setWorkspaces([]);
    }
  }, []);
  useEffect(() => { loadPresets(); loadKits(); loadTeam(); loadWorkspaces(); },
             [loadPresets, loadKits, loadTeam, loadWorkspaces]);

  const savePreset = useCallback(async (kind, section, friendlyName) => {
    if (!section.content?.trim()) {
      toast.error("Add some content before saving a preset.");
      return null;
    }
    try {
      const { data } = await api.post("/manual-presets", {
        kind,
        key: section.key || "",
        title: section.title,
        content: section.content,
        name: (friendlyName || section.title || "").trim() || section.title,
      });
      setPresets((prev) => [data, ...prev]);
      toast.success(`Preset saved: ${data.name}`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  const saveChapterPreset = useCallback(async (chapter, friendlyName) => {
    const hasContent = (chapter.title || chapter.content || chapter.subtitle || chapter.description || "").trim();
    if (!hasContent) {
      toast.error("Add some content before saving a chapter preset.");
      return null;
    }
    try {
      const { data } = await api.post("/manual-presets", {
        kind: "chapter",
        title: chapter.title || "",
        subtitle: chapter.subtitle || "",
        description: chapter.description || "",
        content: chapter.content || "",
        name: (friendlyName || chapter.title || "Chapter preset").trim() || "Chapter preset",
      });
      setPresets((prev) => [data, ...prev]);
      toast.success(`Chapter preset saved: ${data.name}`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  const saveInfoPreset = useCallback(async (fields, name) => {
    const cleaned = Object.fromEntries(Object.entries(fields).filter(([, v]) => v && String(v).trim()));
    if (Object.keys(cleaned).length === 0) {
      toast.error("Pick at least one field with a value to save.");
      return null;
    }
    try {
      const { data } = await api.post("/manual-presets", {
        kind: "info",
        name: (name || "Book info preset").trim(),
        fields: cleaned,
      });
      setPresets((prev) => [data, ...prev]);
      toast.success(`Book-info preset saved: ${data.name}`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  const applyInfoPreset = useCallback((preset) => {
    const f = preset.fields || {};
    setInfo((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }));
    toast.success(`Loaded book-info preset: ${preset.name}`);
  }, []);

  const deletePreset = useCallback(async (presetId) => {
    if (!window.confirm("Delete this preset? This can't be undone.")) return;
    try {
      await api.delete(`/manual-presets/${presetId}`);
      setPresets((prev) => prev.filter((p) => p.id !== presetId));
      toast.success("Preset deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  // ---------- Publisher's Kit ----------
  const applyKit = useCallback(async (kitId) => {
    try {
      const { data } = await api.post(`/preset-kits/${kitId}/apply/${book.id}`);
      // Sync local state from server (patch prop won't re-flow because we own local state here)
      setInfo({
        title: data.title || "", subtitle: data.subtitle || "", author: data.author || "",
        publisher: data.publisher || "", imprint: data.imprint || "",
        copyright_year: data.copyright_year || "", email: data.email || "", isbn: data.isbn || "",
      });
      setFront((data.front_matter_sections || []).map((s) => ({ ...s })));
      setBack((data.back_matter_sections || []).map((s) => ({ ...s })));
      // Only overwrite the intro / toc note when the server returned non-empty
      // — the server-side apply logic honors this same rule.
      if ((data.toc_note || "").trim()) setTocNote(data.toc_note);
      if ((data.introduction || "").trim()) setIntro(data.introduction);
      toast.success("Kit applied");
    } catch (err) { toast.error(formatApiError(err)); }
  }, [book.id]);

  const [importingBack, setImportingBack] = useState(false);
  const importBackMatter = useCallback(async () => {
    setImportingBack(true);
    try {
      const { data } = await api.post(`/books/${book.id}/back-matter/import-from-draft`);
      setBack((data.back_matter_sections || []).map((s) => ({ ...s })));
      await reloadBook?.();
      if (!data.imported && !data.skipped?.length) toast.info("No back matter found in the uploaded draft");
      else toast.success(`Imported ${data.imported} back-matter section(s)` +
        (data.skipped?.length ? ` — kept your existing: ${data.skipped.join(", ")}` : ""));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImportingBack(false); }
  }, [book.id, reloadBook]);

  // iter-44: reset kit-managed fields to defaults. Untangles a mis-applied
  // preset without hand-clearing each front/back section.
  const resetKit = useCallback(async () => {
    try {
      const { data } = await api.post(`/preset-kits/reset/${book.id}`);
      setFront((data.front_matter_sections || []).map((s) => ({ ...s })));
      setBack((data.back_matter_sections || []).map((s) => ({ ...s })));
      setTocNote(""); setIntro("");
      toast.success("Publisher's Kit reset to defaults");
    } catch (err) { toast.error(formatApiError(err)); }
  }, [book.id]);

  const createKit = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/preset-kits", payload);
      setKits((prev) => [data, ...prev]);
      toast.success(`Kit saved: ${data.name}`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  const updateKit = useCallback(async (kitId, payload) => {
    try {
      const { data } = await api.patch(`/preset-kits/${kitId}`, payload);
      setKits((prev) => prev.map((k) => k.id === kitId ? data : k));
      toast.success(`Kit updated: ${data.name}`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  const deleteKit = useCallback(async (kitId) => {
    const k = kits.find((x) => x.id === kitId);
    const label = k ? `"${k.name}"` : "this kit";
    // iter-95c — usage-aware delete confirm (mirrors PresetTemplates).
    let usageLine = "";
    try {
      const { data } = await api.get(`/preset-kits/${kitId}/usage`);
      const n = data.count || 0;
      if (n > 0) {
        const sample = (data.books || []).slice(0, 6).map((b) => `• ${b.title}`).join("\n");
        const more = n > 6 ? `\n(…and ${n - 6} more)` : "";
        usageLine = `\n\nApplied to ${n} book${n === 1 ? "" : "s"}:\n${sample}${more}\n\nNote: their front/back matter, TOC note and intro copy stay in place — this only removes the reusable kit itself.`;
      }
    } catch (_) { /* non-fatal */ }
    if (!window.confirm(`Delete kit ${label}? Its individual presets stay saved.${usageLine}`)) return;
    try {
      await api.delete(`/preset-kits/${kitId}`);
      setKits((prev) => prev.filter((k) => k.id !== kitId));
      toast.success("Kit deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  }, [kits]);

  const setFrontAt = (idx, patchIt) => setFront((prev) => prev.map((s, i) => i === idx ? { ...s, ...patchIt } : s));
  const setBackAt = (idx, patchIt) => setBack((prev) => prev.map((s, i) => i === idx ? { ...s, ...patchIt } : s));

  const setInfoField = (k, v) => setInfo((prev) => ({ ...prev, [k]: v }));

  const dirty = useMemo(() => {
    const initialInfo = {
      title: book.title || "", subtitle: book.subtitle || "", author: book.author || "",
      publisher: book.publisher || "", imprint: book.imprint || "",
      copyright_year: book.copyright_year || "", email: book.email || "", isbn: book.isbn || "",
    };
    // iter-107 — project BOTH sides of the chapter dirty check to the
    // same 6 fields the save actually persists. The local `chapters`
    // state also carries structural helpers (`_kind`, `subtopics`,
    // `chapter_source_index`, `_rid`) that never round-trip through
    // the save equality check on the server — leaving them in the
    // stringify made `dirty` permanently true, which pinned the
    // "Saving…" indicator and re-fired auto-save every 15s forever.
    const projectChapter = (c) => ({
      title: c.title || "", subtitle: c.subtitle || "",
      description: c.description || "", content: c.content || "",
      kind: c.kind || inferKind(c), label: c.label || "",
    });
    return (
      JSON.stringify(info) !== JSON.stringify(initialInfo) ||
      intro !== (book.introduction || "") ||
      tocNote !== (book.toc_note || "") ||
      tocHeading !== (book.toc_heading || "Table of Contents") ||
      JSON.stringify(front) !== JSON.stringify(book.front_matter_sections || []) ||
      JSON.stringify(back) !== JSON.stringify(book.back_matter_sections || []) ||
      JSON.stringify(chapters.map(projectChapter)) !==
        JSON.stringify((book.chapters || []).map(projectChapter)) ||
      chapterNumberingStyle !== (book.chapter_numbering_style || "arabic")
    );
  }, [info, intro, tocNote, tocHeading, front, back, chapters, book, chapterNumberingStyle]);

  const stats = useMemo(() => {
    let words = 0;
    words += countWords(intro) + countWords(tocNote);
    for (const s of front) if (s.include) words += countWords(s.content);
    for (const s of back)  if (s.include) words += countWords(s.content);
    for (const ch of chapters) {
      words += countWords(ch.title) + countWords(ch.subtitle) +
               countWords(ch.description) + countWords(ch.content);
    }
    return { words, readTime: readTime(words) };
  }, [intro, tocNote, front, back, chapters]);

  const save = async () => {
    setBusy(true);
    // iter-39: capture the pre-save book state so authors can revert
    // this entire manual-entry batch (kind/label/chapters/toc/etc.).
    await history.record?.("manual_save",
      `Manual entry saved (${chapters.length} row${chapters.length === 1 ? "" : "s"})`);
    try {
      await patch({
        ...info,
        introduction: intro,
        toc_note: tocNote,
        toc_heading: tocHeading,
        front_matter_sections: front,
        back_matter_sections: back,
        chapters: chapters.map(({ _rid, _autoOpen, ...c }) => c),
        chapter_numbering_style: chapterNumberingStyle,
      });
      toast.success("Manual entry saved");
      // iter-98 — reset the local undo/redo stack after a successful
      // save. Structural undo is scoped to the current editing session;
      // once persisted, "undoing past the save" would silently drop
      // work the author explicitly committed.
      structHistory.reset(chapters);
    } finally { setBusy(false); }
  };

  // iter-94 — "Rebuild formatter" one-click. Explicit escape hatch that
  // re-runs the formatter pipeline against the persisted DOCX source
  // blocks so any classifier upgrades pick up + the export cache is
  // regenerated. Discards manual composer edits to chapter titles /
  // bodies since those come from the source blocks; we warn the user
  // before firing.
  const [rebuilding, setRebuilding] = useState(false);
  const rebuildFormatter = async () => {
    if (rebuilding) return;
    if (dirty && !window.confirm(
        "You have unsaved edits. Rebuilding replaces chapter titles and body text with the current source-block classification. Continue?"
    )) return;
    setRebuilding(true);
    try {
      await api.post(`/books/${book.id}/formatter/reprocess`);
      toast.success("Formatter rebuilt");
      await reloadBook?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setRebuilding(false); }
  };

  const attemptClose = () => {
    if (dirty && !window.confirm("You have unsaved edits in this panel. Discard?")) return;
    onClose();
  };

  // iter-70 — auto-save every 15s while the composer is dirty and idle.
  // Fresh timer on every state change; cleared when we save or unmount.
  useEffect(() => {
    if (!dirty || busy) return;
    const handle = setTimeout(() => { save(); }, 15000);
    return () => clearTimeout(handle);
    // ``save`` closes over the latest state via useState, so re-arming
    // on every dirty change is what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, busy, chapters, info, front, back, intro, tocNote, chapterNumberingStyle]);

  // iter-71 — Live preview follows selection: when the author clicks a
  // Contents row on the left, ``activeIdx`` on the parent bumps, and we
  // scroll the matching chapter card into view inside Manual Entry so
  // both panes visibly track the same chapter.
  useEffect(() => {
    if (activeIdx == null) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="mc-chapter-${activeIdx}-title"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    return () => clearTimeout(t);
  }, [activeIdx]);

  // iter-70/80 — lock / unlock a row from Manual Entry. For imported
  // chapters (numeric ``sourceIndex``) we hit the block-lock endpoint;
  // for Parts / sections / back-matter / manual rows we pass a
  // ``row:{title}`` synthetic key and the row-lock endpoint keeps the
  // state alongside the source-block locks. Both are refreshed via
  // ``reloadBook`` so the sidebar and the card stay in sync.
  const toggleChapterLock = useCallback(async (lockRef, nextLocked) => {
    if (lockRef == null) return;
    try {
      if (typeof lockRef === "number") {
        await api.patch(`/books/${book.id}/structure/${lockRef}/lock`, {
          locked: !!nextLocked,
        });
      } else {
        await api.patch(`/books/${book.id}/rows-lock`, {
          row_key: String(lockRef),
          locked: !!nextLocked,
        });
      }
      if (typeof reloadBook === "function") await reloadBook();
      toast.success(nextLocked ? "Row locked" : "Row unlocked");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [book.id, reloadBook]);

  // iter-71 — bulk lock / unlock every imported chapter. Chapters composed
  // manually (no ``chapter_source_index``) are skipped because the backend
  // block-lock endpoint keys on source index.
  const bulkToggleAllLocks = useCallback(async (nextLocked) => {
    const targetIndices = chapters
      .map((c) => c.chapter_source_index)
      .filter((idx) => idx != null);
    if (!targetIndices.length) {
      toast.info("No imported chapters to lock — Manual Entry rows don't have a source index yet.");
      return;
    }
    try {
      await api.patch(`/books/${book.id}/structure/locks`, {
        source_indices: targetIndices,
        locked: !!nextLocked,
      });
      if (typeof reloadBook === "function") await reloadBook();
      toast.success(nextLocked
        ? `Locked ${targetIndices.length} chapter${targetIndices.length === 1 ? "" : "s"}`
        : `Unlocked ${targetIndices.length} chapter${targetIndices.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [book.id, chapters, reloadBook]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="manual-compose-panel">
      {/* Left dim overlay — only visible when the preview pane is closed
          (composer panel still has a max-width in that mode). When the
          preview is on, the composer + preview fill the whole screen. */}
      {!showPreview && (
        <div className="flex-1 bg-black/40" onClick={attemptClose} />
      )}

      {/* Editor column */}
      <div ref={editorScrollRef} className={`${
              showPreview
                ? "w-1/2 min-w-[420px]"
                : fullWidth ? "w-full" : "w-full max-w-3xl"
            } bg-white shadow-2xl overflow-y-auto border-l border-[color:var(--border)] flex flex-col relative`}
           data-testid="mc-editor-col">
        <header className="sticky top-0 bg-white border-b border-[color:var(--border)] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-medium">
              Manual entry
              {dirty && <span className="ml-2 text-xs px-2 py-1 bg-amber-100 text-amber-800 uppercase tracking-widest align-middle">unsaved</span>}
            </h2>
            <p className="text-xs text-[color:var(--text-muted)] mt-1">
              Every ebook is different — enable only the sections you actually need.
            </p>
            <div className="mt-2 flex items-center gap-4 text-xs" data-testid="mc-stats">
              <span className="inline-flex items-center gap-1">
                <span className="uppercase tracking-widest text-[color:var(--text-muted)]">Words</span>
                <strong data-testid="mc-stats-words" className="font-mono">{stats.words.toLocaleString()}</strong>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="uppercase tracking-widest text-[color:var(--text-muted)]">Est. read time</span>
                <strong data-testid="mc-stats-readtime" className="font-mono">{stats.readTime}</strong>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* iter-90 — Full-width toggle (only useful when the preview
                is hidden, so we hide the button while previewing). */}
            {!showPreview && (
              <button onClick={()=>setFullWidth((v)=>!v)}
                className={`text-sm inline-flex items-center gap-1 px-2 py-1 border ${fullWidth ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]" : "border-[color:var(--border)] hover:bg-[color:var(--canvas)]"}`}
                data-testid="mc-fullwidth-btn"
                aria-pressed={fullWidth}
                title={fullWidth ? "Return to reading-width layout" : "Expand Manual Entry to full width"}>
                {fullWidth ? <Minimize2 className="w-4 h-4"/> : <Maximize2 className="w-4 h-4"/>}
                {fullWidth ? "Reading width" : "Full width"}
              </button>
            )}
            {/* iter-90 — Design panel toggle in the header. Always
                accessible so the panel isn't stranded when preview is off. */}
            {bookId && (
              <button onClick={()=>setShowDesign((v)=>!v)}
                className={`text-sm inline-flex items-center gap-1 px-2 py-1 border ${showDesign ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]" : "border-[color:var(--border)] hover:bg-[color:var(--canvas)]"}`}
                data-testid="mc-design-btn"
                aria-pressed={showDesign}
                title={showDesign ? "Hide the Design panel" : "Show the Design panel"}>
                <Palette className="w-4 h-4"/>
                {showDesign ? "Hide design" : "Design"}
              </button>
            )}
            {/* iter-92 — Reading Ruler toggle */}
            <button onClick={()=>setShowRuler((v)=>!v)}
              className={`text-sm inline-flex items-center gap-1 px-2 py-1 border ${showRuler ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]" : "border-[color:var(--border)] hover:bg-[color:var(--canvas)]"}`}
              data-testid="mc-ruler-btn"
              aria-pressed={showRuler}
              title={showRuler ? "Hide the reading ruler" : "Show a movable ruler for eyeballing page breaks"}>
              <Ruler className="w-4 h-4"/>
              {showRuler ? "Hide ruler" : "Ruler"}
            </button>
            <button onClick={()=>setShowPreview((v)=>!v)}
              className={`text-sm inline-flex items-center gap-1 px-2 py-1 border ${showPreview ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]" : "border-[color:var(--border)] hover:bg-[color:var(--canvas)]"}`}
              data-testid="mc-preview-btn"
              aria-pressed={showPreview}
              title={showPreview ? "Hide side-by-side preview" : "Show side-by-side preview"}>
              {showPreview ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              {showPreview ? "Hide preview" : "Preview"}
            </button>
            <button onClick={save} disabled={busy || !dirty}
              className="btn-primary text-sm" data-testid="mc-save-btn">
              {busy ? "Saving…" : "Save"}
            </button>
            {/* iter-94 — Rebuild formatter escape hatch. Only surfaced
                for books that were originally uploaded via DOCX (they
                carry a `formatter_v2_report`). Personal / manual books
                have nothing to rebuild from. */}
            {book?.formatter_v2_report ? (
              <button onClick={rebuildFormatter} disabled={rebuilding}
                className="btn-ghost text-sm inline-flex items-center gap-1"
                data-testid="mc-rebuild-btn"
                title="Re-run the formatter pipeline against the original manuscript. Replaces chapter titles and body text with the current classifier output.">
                <RefreshCw className={`w-4 h-4 ${rebuilding ? "animate-spin" : ""}`}/>
                {rebuilding ? "Rebuilding…" : "Rebuild formatter"}
              </button>
            ) : null}
            <button onClick={attemptClose} className="btn-ghost inline-flex items-center gap-1" data-testid="manual-compose-close">
              <X className="w-4 h-4"/> Close
            </button>
          </div>
        </header>

        {/* iter-92 — Reading Ruler overlay. Absolute-positioned inside
            the editor column so it moves with the canvas scroll. */}
        <ReadingRuler
          containerRef={editorScrollRef}
          enabled={showRuler}
          totalWords={stats.words}
          wordsPerPage={275}
        />

        <div className="p-6 space-y-8">
          {/* Publisher's Kit — bundle of presets applied with one click */}
          <PublishersKitBar
            kits={kits} presets={presets} team={team} workspaces={workspaces}
            currentTocNote={tocNote} currentIntroduction={intro}
            onApplyKit={applyKit} onDeleteKit={deleteKit}
            onCreateKit={createKit} onUpdateKit={updateKit}
            onResetKit={resetKit}
          />

          {/* Book info */}
          <Section title="§ Book info" defaultOpen>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-[color:var(--text-muted)]">
                Publisher / imprint / boilerplate — save the combo once, load it into every future book.
              </p>
              <BookInfoPresetControls
                presets={presets.filter((p)=>p.kind === "info")}
                currentInfo={info}
                onSave={saveInfoPreset}
                onLoad={applyInfoPreset}
                onDelete={deletePreset}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {BOOK_INFO_FIELDS.map(([k, label, type]) => (
                <label key={k} className={k === "isbn" || k === "subtitle" ? "col-span-2 block" : "block"}>
                  <span className="block text-xs uppercase tracking-widest mb-1">{label}</span>
                  <input
                    type={type} value={info[k]}
                    onChange={(e)=>setInfoField(k, e.target.value)}
                    className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                    data-testid={`mc-info-${k}`}
                  />
                </label>
              ))}
            </div>
          </Section>

          {/* TOC heading + note */}
          <Section title="§ Table of Contents">
            {/* iter-107 — Author-editable TOC page heading. Some
                non-fiction authors deliberately prefer "In This Book"
                or "What's Inside" over the standard label. */}
            <label className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">
              TOC page heading
            </label>
            <input type="text" value={tocHeading}
              onChange={(e)=>setTocHeading(e.target.value)}
              placeholder="Table of Contents"
              className="w-full border border-[color:var(--border)] px-3 py-2 mb-2 focus:outline-none focus:border-[color:var(--primary)]"
              data-testid="mc-toc-heading"/>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
                Editorial note (optional)
              </label>
              {tocHeading !== "Table of Contents" && (
                <button type="button" onClick={()=>setTocHeading("Table of Contents")}
                  className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--primary)] underline"
                  title="Reset to the default heading"
                  data-testid="mc-toc-heading-reset">
                  Reset to default
                </button>
              )}
            </div>
            <textarea rows={2} value={tocNote} onChange={(e)=>setTocNote(e.target.value)}
              placeholder="A short editorial note printed above the Table of Contents (optional)."
              className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
              data-testid="mc-toc-note"/>
          </Section>

          {/* Introduction — iter-109: switched from a plain
              <textarea> to the chapter-grade RichBodyEditor so authors
              can bold / italic / add bullets / insert links / paste
              from Google Docs & Word with formatting preserved all the
              way through to EPUB + PDF. Both backend paragraph
              builders (`_manual_paragraphs_html` and
              `_epub_paragraphs_html`) already detect inline HTML and
              sanitize via bleach, so no backend change is needed. */}
          <Section title="§ Introduction">
            <p className="text-xs text-[color:var(--text-muted)] mb-2">
              Set the reader up. Why this book exists, who it's for, what they'll
              take away. Bold, italic, bullets, headings and links are supported.
            </p>
            <RichBodyEditor
              value={intro}
              onChange={setIntro}
              permissive={(book?.paste_style_mode || "strict") === "permissive"}
              bookId={bookId}
              testId="mc-introduction"
              minHeight={200}
              placeholder="Set the reader up. Why this book exists, who it's for, what they'll take away."
            />
          </Section>

          {/* Chapters — manual entry (title/subtitle/description/content) */}
          <Section title={`§ Chapters (${chapters.length})`} defaultOpen>
            <p className="text-xs text-[color:var(--text-muted)] mb-3">
              Add every chapter of your book. The <strong>Title</strong>, <strong>Subtitle</strong>,
              and <strong>Description</strong> feed the Table of Contents; the <strong>Content</strong>
              is the chapter body. TOC entries link directly to the matching chapter in the exported PDF/EPUB.
              <span className="block mt-1 text-[10px] text-[color:var(--text-muted)]">
                Tip: drag the grip handle on the left of any chapter to reorder — or use the arrow buttons.
              </span>
            </p>
            {/* iter-71 — bulk lock quick-actions. Only surfaced when the
                book has at least one imported chapter (chapter_source_index
                != null); manual-only books have nothing to lock yet. */}
            {chapters.some((c) => c.chapter_source_index != null) && (
              <div className="mb-3 flex items-center gap-2 text-xs">
                <span className="uppercase tracking-widest text-[color:var(--text-muted)]">
                  Bulk actions
                </span>
                <button
                  type="button"
                  onClick={() => bulkToggleAllLocks(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--border)] bg-white hover:bg-[color:var(--canvas)]"
                  data-testid="mc-bulk-lock-all"
                >
                  <Lock className="w-3 h-3"/> Lock all chapters
                </button>
                <button
                  type="button"
                  onClick={() => bulkToggleAllLocks(false)}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--border)] bg-white hover:bg-[color:var(--canvas)]"
                  data-testid="mc-bulk-unlock-all"
                >
                  <Unlock className="w-3 h-3"/> Unlock all
                </button>
              </div>
            )}
            <div className="space-y-3">
              {(() => {
                const labels = computeChapterLabels(chapters, chapterNumberingStyle);
                return chapters.map((c, idx) => (
                  <ChapterCard
                    key={c._rid}
                    index={idx}
                    chapter={c}
                    computedLabel={labels[idx]?.label || ""}
                    permissive={(book?.paste_style_mode || "strict") === "permissive"}
                    bookId={book?.id || ""}
                    isFirst={idx === 0}
                    isLast={idx === chapters.length - 1}
                    presets={presets}
                    onChange={(patchIt)=>setChapterAt(idx, patchIt)}
                    onMove={(dir)=>moveChapter(idx, dir)}
                    onRemove={()=>removeChapter(idx)}
                    onSavePreset={(name)=>saveChapterPreset(c, name)}
                    onDeletePreset={deletePreset}
                    /* iter-70 — one lock source of truth. Read the
                       backend map that also feeds the left Contents
                       sidebar so toggling here updates both sides. */
                    locked={(() => {
                      // iter-70/80 — a row is "locked" if the DOCX
                      // source-block map OR the row-key map says so.
                      const src = c.chapter_source_index;
                      const blocks = (book?.formatter_v2_locked_blocks || {});
                      const rows = (book?.formatter_v2_locked_rows || {});
                      if (src != null && blocks[String(src)]) return true;
                      const rowKey = `row:${(c.title || "").trim() || `idx-${idx}`}`;
                      return !!rows[rowKey];
                    })()}
                    onToggleLock={toggleChapterLock}
                    /* iter-71 — front/back matter reclassify inline on the
                       chapter row so authors never leave Manual Entry. */
                    onReclassifyToFrontBack={onReclassifyChapter
                      ? (target) => onReclassifyChapter(idx, target)
                      : null}
                    isDragging={dragIdx === idx}
                    isDragTarget={dragOverIdx === idx && dragIdx !== null && dragIdx !== idx}
                    onDragStart={()=>setDragIdx(idx)}
                    onDragEnter={()=>{ if (dragIdx !== null) setDragOverIdx(idx); }}
                    onDragEnd={()=>{ setDragIdx(null); setDragOverIdx(null); }}
                    onDrop={()=>{
                      if (dragIdx !== null && dragIdx !== idx) reorderChapter(dragIdx, idx);
                      setDragIdx(null); setDragOverIdx(null);
                    }}
                  />
                ));
              })()}
              {chapters.length === 0 && (
                <p className="text-xs text-[color:var(--text-muted)] italic p-3 border border-dashed border-[color:var(--border)]">
                  No chapters yet. Click "Add chapter" below to start entering them one at a time.
                </p>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button type="button" onClick={addChapter}
                className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="mc-add-chapter">
                <Plus className="w-4 h-4"/> Add chapter
              </button>
              <button type="button" onClick={addPart}
                className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="mc-add-part">
                <Plus className="w-4 h-4"/> Add part
              </button>
              <button type="button" onClick={addSection}
                className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="mc-add-section">
                <Plus className="w-4 h-4"/> Add section
              </button>
              <button type="button" onClick={addPreface}
                className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="mc-add-preface">
                <Plus className="w-4 h-4"/> Add preface
              </button>
              <button type="button" onClick={addAppendix}
                className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="mc-add-appendix">
                <Plus className="w-4 h-4"/> Add appendix
              </button>
              {/* iter-98 — Structural undo / redo. Snapshots every
                  add / delete / reorder / kind change so authors can
                  experiment freely. Content typing is left alone so
                  native input undo still works inside text fields. */}
              <span className="mx-1 h-4 w-px bg-[color:var(--border)]" aria-hidden/>
              <button type="button" onClick={structHistory.undo}
                      disabled={!structHistory.canUndo}
                      className={`text-sm inline-flex items-center gap-1 ${structHistory.canUndo ? "btn-ghost" : "text-[color:var(--text-muted)] cursor-not-allowed opacity-50"}`}
                      title={`Undo structural change (${navigator.platform.startsWith("Mac") ? "⌘Z" : "Ctrl+Z"})`}
                      data-testid="mc-undo-structure">
                <Undo2 className="w-4 h-4"/> Undo
                {structHistory.past.length > 0 && (
                  <span className="text-[10px] text-[color:var(--text-muted)] ml-0.5">
                    {structHistory.past.length}
                  </span>
                )}
              </button>
              <button type="button" onClick={structHistory.redo}
                      disabled={!structHistory.canRedo}
                      className={`text-sm inline-flex items-center gap-1 ${structHistory.canRedo ? "btn-ghost" : "text-[color:var(--text-muted)] cursor-not-allowed opacity-50"}`}
                      title={`Redo structural change (${navigator.platform.startsWith("Mac") ? "⇧⌘Z" : "Ctrl+Y"})`}
                      data-testid="mc-redo-structure">
                <Redo2 className="w-4 h-4"/> Redo
                {structHistory.future.length > 0 && (
                  <span className="text-[10px] text-[color:var(--text-muted)] ml-0.5">
                    {structHistory.future.length}
                  </span>
                )}
              </button>
              <span className="text-[11px] text-[color:var(--text-muted)]">
                Parts group chapters (Roman-numbered, no chapter count).
                Sections are un-numbered TOC entries like Resources.
                Appendices sit after the last chapter with no P/§/number label.
                Prefaces sit at the top of the book before Chapter 1 — add multiple for
                translator's notes, "How to use this book", etc.
              </span>
            </div>
            {/* iter-38: chapter numbering style dropdown. Parts always
                stay Roman regardless of the choice here. */}
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-[color:var(--border)] pt-3">
              <label className="text-xs flex items-center gap-2">
                <span className="uppercase tracking-widest text-[color:var(--text-muted)]">
                  Chapter numbering
                </span>
                <select
                  value={chapterNumberingStyle}
                  onChange={(e) => setChapterNumberingStyle(e.target.value)}
                  className="border border-[color:var(--border)] px-2 py-1 text-xs bg-white focus:outline-none focus:border-[color:var(--primary)]"
                  data-testid="mc-chapter-numbering-style"
                >
                  <option value="arabic">Arabic — Chapter 1, 2, 3</option>
                  <option value="roman">Roman — Chapter I, II, III</option>
                  <option value="words">Words — Chapter One, Two</option>
                  <option value="none">None — just "Chapter"</option>
                </select>
              </label>
              <span className="text-[11px] text-[color:var(--text-muted)]">
                Parts always use Roman numerals regardless.
              </span>
            </div>
          </Section>

          {/* Front matter — canonical toggleable list */}
          <Section title="§ Front matter" defaultOpen>
            <p className="text-xs text-[color:var(--text-muted)] mb-3">
              Toggle each section to include it in the exported book. Save any section as a preset to reuse it in future books.
            </p>
            <div className="space-y-3">
              {front.map((s, idx) => (
                <SectionCard
                  key={s.key || `f-${idx}`}
                  section={s} testIdPrefix="mc-front" kind="front"
                  presets={presets}
                  onChange={(patchIt)=>setFrontAt(idx, patchIt)}
                  onSavePreset={(name)=>savePreset("front", s, name)}
                  onDeletePreset={deletePreset}
                />
              ))}
            </div>
          </Section>

          {/* Back matter — canonical toggleable list */}
          <Section title="§ Back matter" defaultOpen>
            <p className="text-xs text-[color:var(--text-muted)] mb-3">
              Same as front matter — flip on the ones you need. All are optional.
            </p>
            {book?.formatter_v2_report && (
              <button onClick={importBackMatter} disabled={importingBack}
                className="btn-ghost text-xs inline-flex items-center gap-1 mb-3 disabled:opacity-40"
                data-testid="mc-back-import-draft-btn">
                <Download size={12} /> {importingBack ? "Importing…" : "Import back-matter from draft"}
              </button>
            )}
            <div className="space-y-3">
              {back.map((s, idx) => (
                <SectionCard
                  key={s.key || `b-${idx}`}
                  section={s} testIdPrefix="mc-back" kind="back"
                  presets={presets}
                  onChange={(patchIt)=>setBackAt(idx, patchIt)}
                  onSavePreset={(name)=>savePreset("back", s, name)}
                  onDeletePreset={deletePreset}
                />
              ))}
            </div>
          </Section>

          <div className="pt-4 pb-8 border-t border-[color:var(--border)]">
            <button onClick={save} disabled={busy || !dirty}
              className="btn-primary" data-testid="mc-save-btn-bottom">
              {busy ? "Saving…" : "Save all"}
            </button>
          </div>
        </div>
      </div>

      {/* Side-by-side preview pane — always open when Preview toggle is on. */}
      {showPreview && (
        <div className="flex-1 bg-[color:var(--canvas)] overflow-y-auto border-l border-[color:var(--border)]"
             data-testid="mc-preview-pane">
          <div className="sticky top-0 bg-white border-b border-[color:var(--border)] px-6 py-3 flex items-center justify-between z-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Live preview</p>
              <p className="text-xs text-[color:var(--text-muted)]">
                Renders exactly what the exported PDF / EPUB will contain — updates as you type.
              </p>
            </div>
            <button onClick={()=>setShowPreview(false)}
              className="btn-ghost text-sm inline-flex items-center gap-1"
              data-testid="mc-preview-close-btn"
              title="Hide the preview and return to full-width editor">
              <EyeOff className="w-4 h-4"/> Hide
            </button>
          </div>
          <div className="bg-white mx-auto my-6 max-w-3xl shadow-sm border border-[color:var(--border)]"
                style={designPreviewStyle}>
            <BookPreviewPane
              info={info}
              tocNote={tocNote}
              intro={intro}
              front={front}
              back={back}
              chapters={chapters}
              chapterNumberingStyle={chapterNumberingStyle}
              designControls={designControls}
              coverUrl={book?.cover_url || book?.cover_data_url}
              coverFocalPoint={book?.cover_focal_point}
              layoutClass="p-8 md:p-12 space-y-14"
            />
          </div>
        </div>
      )}

      {/* iter-81 — Design controls right column. Always visible when
          Manual Entry is open so the author can tweak sizes, fonts and
          spacings and watch the live preview update.
          iter-83 — collapsible via the Hide button so authors can
          reclaim the horizontal space when needed.
          iter-90 — decoupled from `showPreview` so hiding the preview
          no longer strands the Design panel with no way to reopen it. */}
      {bookId && showDesign && (
        <div className="w-[320px] shrink-0 bg-white overflow-y-auto border-l border-[color:var(--border)]"
             data-testid="mc-design-pane">
          <div className="p-3">
            <DesignControlsPanel
              bookId={bookId}
              initial={designControls}
              initialTemplateId={book?.design_template_id}
              initialLocked={book?.design_locked}
              onLiveChange={setDesignControls}
              onHide={()=>setShowDesign(false)}
            />
          </div>
        </div>
      )}
      {bookId && !showDesign && (
        <button onClick={()=>setShowDesign(true)}
                className="shrink-0 w-8 bg-[color:var(--canvas)] border-l border-[color:var(--border)] flex items-center justify-center hover:bg-white"
                data-testid="mc-design-show"
                title="Show the Design panel">
          <span className="uppercase tracking-widest text-[9px] text-[color:var(--text-muted)] [writing-mode:vertical-lr] py-4">
            Design
          </span>
        </button>
      )}
    </div>
  );
}


